import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import cn from 'classnames';
import { Button } from '@fiverr-private/button';
import { createSanitizedMarkup } from '@fiverr-private/futile';
import {
  translateSubCategory,
  SubCategoryAttribute,
} from '@fiverr-private/translation_utils';
import { interpolatePartner, getCertifiedPartnerHomeHref } from '../../utils';
import { CertifiedBannerApi } from '../../server';
import { useEvents, UseEventsOptions } from '../../hooks/useEvents';
import { Logo, LogoProps } from '../Logo';
import { Picture } from './assets/Picture';
import styles from './CertifiedBannerClassic.module.scss';

/**
 * The default test id for tests.
 */
export const CERTIFIED_BANNER_CLASSIC_DEFAULT_TEST_ID =
  'certified-banner-classic';

/**
 * CertifiedBanner props interface.
 */
export interface CertifiedBannerClassicProps
  extends Pick<CertifiedBannerApi, 'subCategoryId' | 'description' | 'cta'>,
    Pick<UseEventsOptions, 'searchTerm' | 'pageName'>,
    Pick<LogoProps, 'logoUrl'> {
  /**
   * Custom `data-testid` attribute value for CertifiedBanner wrapper `div` element, used in tests.
   */
  testId?: string;
  /**
   * Custom `class` attribute value for CertifiedBanner wrapper `div` element.
   */
  className?: string;
}
/**
 * Banner component to promote Fiverr Certified on search and tag pages.
 */
export const CertifiedBannerClassic = forwardRef<
  HTMLDivElement,
  CertifiedBannerClassicProps
>(
  (
    {
      subCategoryId,
      logoUrl,
      description,
      cta,
      searchTerm,
      testId = CERTIFIED_BANNER_CLASSIC_DEFAULT_TEST_ID,
      className,
      pageName,
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);

    const partnerName = translateSubCategory({ subCategoryId });

    const partnerSlug = translateSubCategory({
      subCategoryId,
      subCategoryAttribute: SubCategoryAttribute.Slug,
    });

    const homeHref = getCertifiedPartnerHomeHref(partnerSlug);

    const trackClick = useEvents({
      ref: innerRef,
      partnerSlug,
      partnerId: subCategoryId,
      searchTerm,
      pageName,
    });

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
      ref,
      () => innerRef.current
    );

    return (
      <div
        data-testid={testId}
        ref={innerRef}
        className={cn(
          className,
          styles.root,
          'flex',
          'flex-between',
          'co-text-darkest'
        )}
      >
        <div className={cn(styles.left, 'flex')}>
          <Picture className={styles.picture} />
          <div>
            <Logo
              logoUrl={logoUrl}
              className={styles.logo}
              partnerName={partnerName}
            />
            <p
              className={cn(styles.text, 'tbody-3')}
              dangerouslySetInnerHTML={createSanitizedMarkup(
                interpolatePartner(description, partnerName)
              )}
            />
          </div>
        </div>
        <div className={styles.right}>
          <Button
            href={homeHref}
            onClick={trackClick}
            className={styles.button}
          >
            {cta}
          </Button>
        </div>
      </div>
    );
  }
);

CertifiedBannerClassic.displayName = 'CertifiedBannerClassic';
